import type { SelectionRange } from "../global";
import htmx from "htmx.org";
import { AlpineComponent } from "@nxtlvlsoftware/alpine-typescript";
import SearchResult from "./SearchResult.ts";
import { selectionHandler } from "../handlers/selection.ts";
import { dispatch } from "../utils/events.ts";
import { onOfficeReady } from "../office/state"
import { SEARCH_POINTERDOWN_TIMEOUT_MS } from "../timing";

export default class SearchBar extends AlpineComponent {
  constructor(
    public url: string,
    public query: string,
    public index: number,
    public focus: boolean = false,
    public selection: SelectionRange | null = null,
    public enable = true,
    public hasResults = false,
    public fromSelection = false,
  ) {
    super();
  }

  init() {
    this.index = 0
    this.hasResults = document.getElementById("search-results") !== null;

    onOfficeReady(async (enabled) => {
      if (enabled) {
        await this.toggleEditorSelection()
      }
    })
  }

  destroy() {
  }

  onPressTab(e: KeyboardEvent) {
    e.preventDefault()
    e.stopImmediatePropagation()
    this.$refs.query.focus()
  }

  onPressEscape() {
    this.$refs.query.blur()
  }

  onClickTab() {
    this.$refs.query.focus()
  }

  onHtmxAfterRequest(e: CustomEvent<{ xhr: XMLHttpRequest }>) {
    if (e.detail.xhr.responseURL.endsWith(this.getUrl()) && e.detail.xhr.status == 200) {
      this.hasResults = true
    }
  }

  async onSearchResultSubmitted() {
    this.focus = false
    this.query = "";
    this.index = 0;
    this.hasResults = false;
    this.selection = null
    this.$refs.query.blur()
    await this.deactivateEditorSelection()
    dispatch("search-query", "");
  }

  onSearchResultSelected(e: CustomEvent<SearchResult>) {
    this.index = e.detail.index;
  }

  async onSearchSelectButton() {
    await this.deactivateEditorSelection()
  }

  async onSearchEditButton() {
    this.query = ""
    this.index = 0
    this.hasResults = true
    await this.activateEditorSelection()
    await this.update()
  }

  async onSearchCancelButton() {
    this.query = ""
    this.hasResults = false
    this.focus = false
    await this.deactivateEditorSelection()
  }

  async onInput() {
    this.focus = true;
    this.fromSelection = false
    await this.toggleEditorSelection()
    await this.update();
  }

  onInputBlur() {
    this.focus = false;
  }

  async onInputFocus() {
    if (!this.enable || await SearchBar.isFalsePositiveFocus()) return

    // the search bar is empty and we are in office,
    // we start watching the selection
    await this.toggleEditorSelection();
    this.focus = true;
    this.fromSelection = false
    await this.update()
  }

  async onClickClear() {
    this.query = ""
    await this.update()
  }

  async onDocumentSelectionChanged(e: CustomEvent<SelectionRange>) {
    if (JSON.stringify(this.selection) === JSON.stringify(e.detail)) return
    this.selection = e.detail;
    this.fromSelection = true
    await this.update()
  }

  getUrl() {
    var qs = new URLSearchParams()
    qs.set("query", this.query ?? "")
    if (this.index !== undefined) {
      qs.set("index", this.index.toString())
      qs.set("fromSelection", this.fromSelection.toString())
    }
    return `${this.url}?${qs.toString()}`
  }


  private async update() {
    const url = this.getUrl()
    await htmx.ajax('post', url, {
      values: { selection: this.selection },
    });
  }


  private async toggleEditorSelection() {
    if (selectionHandler.phase === 'unregistered') return
    if (this.query.length == 0) {
      await this.activateEditorSelection()
    } else {
      await this.deactivateEditorSelection()
    }
  }

  private async deactivateEditorSelection() {
    this.selection = null
    await this.$nextTick();
    selectionHandler.pause()
  }

  private async activateEditorSelection() {
    // set the selection value
    if (selectionHandler.factory !== null) {
      this.selection = await selectionHandler.factory.buildRange();
    }

    // we await the next render so the input[val] is updated
    await this.$nextTick();
    selectionHandler.resume()
  }

  /**
   * Detects false positive focus events caused by WebView2 focus behavior.
   * 
   * When a user clicks from the main Microsoft Word document into the TaskPane,
   * WebView2 fires a focus event on the clicked element BEFORE the pointerdown event.
   * This creates a race condition where the search input receives focus, triggers a
   * search with the current document selection, and then immediately loses focus
   * when the pointerdown event fires.
   * 
   * This method uses a 10ms timeout to detect if a pointerdown event follows the
   * focus event. If pointerdown fires within the timeout, it indicates the user
   * was clicking into the TaskPane (not intentionally focusing the search bar),
   * so we return true to suppress the focus action.
   * 
   * Only applies to Office on Windows (PC platform) where this WebView2 behavior
   * is observed. Returns false immediately for other platforms or when Office
   * integration is not enabled.
   */
  static isFalsePositiveFocus(): Promise<boolean> {
    if (typeof Office === 'undefined' || Office.context?.platform !== Office.PlatformType.PC) {
      return Promise.resolve(false)
    }

    return new Promise<boolean>(resolve => {
      const timeoutId = window.setTimeout(() => {
        window.removeEventListener("pointerdown", handler)
        resolve(false)
      }, SEARCH_POINTERDOWN_TIMEOUT_MS)
      const handler = () => {
        window.clearTimeout(timeoutId)
        window.removeEventListener("pointerdown", handler)
        resolve(true)
      }
      window.addEventListener("pointerdown", handler)
    })
  }
}
